import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAllSuggestions } from '../../store/suggest/suggest.selectors'
import { KEYS } from '../../utils/keyboard.constants'
import { selectIsUserConnected, selectIsUserAccountEnabled } from '../../store/userAccount/userAccount.selectors'
import SuggestFavoritesContent from './SuggestFavoritesContent'
import SuggestSearchHistoryContent from './SuggestSearchHistoryContent'
import SuggestServiceContent from './SuggestServiceContent'
import SuggestClearHistory from './SuggestClearHistory'
import useIncentiveClick from './useIncentiveClick'
import SuggestContentError from './SuggestContentError'
import s from './SuggestContent.less'
import { TAG_SUGGEST } from '../analytics/analytics.tagids.constants'
import { isEmpty } from '../../utils/lang'
import { addDatadogRumError } from '../rum/DataDogRum'

const DEBOUNCE_DURATION = 150

const SuggestContent = ({ onClick, positionComponent, withoutFavorites = false }) => {
  const { suggestions, error } = useSelector(selectAllSuggestions(withoutFavorites))
  const isUserAccountAvailable = useSelector(selectIsUserAccountEnabled)
  const isConnected = useSelector(selectIsUserConnected)
  const [selectedId, setSelectedId] = useState(null)
  const [timestamp, setTimestamp] = useState(null)
  const hasFavoriteSuggests = !isConnected ? isUserAccountAvailable : !isEmpty(suggestions.favorites)
  const hasSearchHistorySuggests = !isEmpty(suggestions.searchHistory)
  const hasServiceSuggests = !isEmpty(suggestions.service)
  const hasSuggests = hasFavoriteSuggests || hasSearchHistorySuggests || hasServiceSuggests
  const stateRef = useRef({
    suggestsRef: suggestions,
    errorRef: error,
    selectedIdRef: selectedId,
    timestampRef: timestamp
  })

  const incentiveTagClickBuilder = useCallback(() => {
    if (isConnected) return <></>
    return { type: TAG_SUGGEST }
  }, [isConnected])

  const { onIncentiveClick } = useIncentiveClick({ onClick, incentiveTagClickBuilder })

  useEffect(() => {
    if (__SERVER__) return
    document.addEventListener('keydown', handleKeyboardEvent, false)

    return () => {
      document.removeEventListener('keydown', handleKeyboardEvent, false)
    }
  }, [])

  useEffect(() => {
    stateRef.current = {
      ...stateRef.current,
      suggestsRef: suggestions,
      errorRef: error
    }
  }, [suggestions, error])

  const updateSelectedId = idx => {
    const now = Date.now()
    setSelectedId(idx)
    setTimestamp(now)
    stateRef.current = {
      ...stateRef.current,
      selectedIdRef: idx,
      timestampRef: now
    }
  }

  const hasSpeedScrollLimitReached = (currentTimestamp, newTimestamp, key) => {
    return (
      currentTimestamp &&
      newTimestamp - currentTimestamp < DEBOUNCE_DURATION &&
      (key === KEYS.DOWN || key === KEYS.UP || key === KEYS.ENTER || key === KEYS.TAB)
    )
  }

  const handleKeyboardEvent = e => {
    const { suggestsRef, errorRef, selectedIdRef, timestampRef } = stateRef.current

    if (errorRef) {
      return
    }

    const currentSelectedId = selectedIdRef
    const currentTimestamp = timestampRef
    let newSelectedId
    let newTimestamp = Date.now()
    const suggestsSize = suggestsRef.favorites.length + suggestsRef.service.length + suggestsRef.searchHistory.length
    const isKeyDown = e.key === KEYS.DOWN || (!e.shiftKey && e.key === KEYS.TAB)
    const isKeyUp = e.key === KEYS.UP || (e.shiftKey && e.key === KEYS.TAB)
    const isKeyEnter = e.key === KEYS.ENTER

    // handle speed scrolling with arrow
    if (hasSpeedScrollLimitReached(currentTimestamp, newTimestamp, e.key)) {
      return e.preventDefault()
    }

    if (isKeyDown) {
      e.preventDefault()
      newSelectedId = currentSelectedId !== null ? (currentSelectedId + 1) % suggestsSize : 0
    } else if (isKeyUp) {
      e.preventDefault()
      newSelectedId =
        currentSelectedId !== null
          ? currentSelectedId - 1 >= 0
            ? currentSelectedId - 1
            : suggestsSize - 1
          : suggestsSize - 1
    } else if (isKeyEnter && currentSelectedId !== null) {
      e.preventDefault()
      e.stopPropagation()
      newSelectedId = null
      const allSuggests = [...suggestsRef.favorites, ...suggestsRef.searchHistory, ...suggestsRef.service]
      const suggest = allSuggests[currentSelectedId]
      if (suggest) {
        return onIncentiveClick(suggest)
      } else {
        addDatadogRumError(new Error(`suggest not found by selectedId`, { currentSelectedId }))
      }
    } else if (e.shiftKey && e.key === KEYS.SHIFT) {
      // Condition pour éviter de perdre la position de l'item sélectionné, quand on appuie que sur shift
      e.preventDefault()
      newSelectedId = currentSelectedId
      newTimestamp = currentTimestamp
    } else {
      newTimestamp = null
      newSelectedId = null
    }

    setSelectedId(newSelectedId)
    setTimestamp(newTimestamp)
    stateRef.current = {
      ...stateRef.current,
      selectedIdRef: newSelectedId,
      timestampRef: newTimestamp
    }
  }

  return (
    <div className={s.SuggestContent}>
      {positionComponent}

      <SuggestFavoritesContent
        suggestionsFavorites={suggestions.favorites}
        idxStartAt={0}
        selectedId={selectedId}
        updateSelectedId={updateSelectedId}
        onClick={onIncentiveClick}
      />

      <SuggestSearchHistoryContent
        suggestionsSearchHistory={suggestions.searchHistory}
        idxStartAt={suggestions.favorites.length}
        selectedId={selectedId}
        updateSelectedId={updateSelectedId}
        onClick={onClick}
      />

      <SuggestServiceContent
        suggestionsService={suggestions.service}
        idxStartAt={suggestions.favorites.length + suggestions.searchHistory.length}
        selectedId={selectedId}
        updateSelectedId={updateSelectedId}
        onClick={onClick}
      />

      <SuggestClearHistory suggestionsSearchHistory={suggestions.searchHistory} />

      <SuggestContentError hasSuggests={hasSuggests} error={error} />
    </div>
  )
}

export default SuggestContent
